import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  findProfileForRole,
  getProfileDashboardPath,
  getRememberedProfileId,
  getRoleDashboardPath,
  profileToRole,
} from '@/lib/authRouting';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@ergshoot/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// 5 canonical display tabs — Coach merges into Owner (both go to /affiliate)
const CANONICAL_ROLES: {
  key: string;
  label: string;
  mappedRoles: UserRole[];
  disabledTooltip: string;
  disabledLink?: string;
}[] = [
  {
    key: 'admin',
    label: 'HQ Admin',
    mappedRoles: ['hq_admin'],
    disabledTooltip: 'HQ Admin access is by invitation only',
  },
  {
    key: 'agent',
    label: 'Agent',
    mappedRoles: ['agent'],
    disabledTooltip: 'Interested in becoming an agent?',
    disabledLink: '/apply/agent',
  },
  {
    key: 'owner',
    label: 'Owner',
    mappedRoles: ['affiliate_owner', 'affiliate_coach'],
    disabledTooltip: 'Apply to run ErgShoot at your gym',
    disabledLink: '/apply/affiliate',
  },
  {
    key: 'member',
    label: 'Member',
    mappedRoles: ['member'],
    disabledTooltip: 'Members are invited by their gym',
    disabledLink: '/find-gym',
  },
  {
    key: 'individual',
    label: 'Individual',
    mappedRoles: ['individual'],
    disabledTooltip: 'Subscribe to train independently',
    disabledLink: '/signup/individual',
  },
];

export function RoleToggleBar() {
  const user = useAuthStore((s) => s.user);
  const profiles = useAuthStore((s) => s.profiles);
  const activeProfile = useAuthStore((s) => s.activeProfile);
  const sessionActiveRoles = useAuthStore((s) => s.sessionActiveRoles);
  const setSessionActiveRoles = useAuthStore((s) => s.setSessionActiveRoles);
  const switchProfile = useAuthStore((s) => s.switchProfile);
  const navigate = useNavigate();
  const [switchingRole, setSwitchingRole] = useState<UserRole | null>(null);

  // Collect all actual UserRoles the user has from profiles
  const userRolesSet = useMemo((): Set<UserRole> => {
    const roles = new Set<UserRole>();
    if (user?.role) roles.add(user.role);
    for (const profile of profiles) {
      if (profile.is_active !== false) {
        roles.add(profileToRole(profile));
      }
    }
    return roles;
  }, [user?.role, profiles]);

  // Which canonical tabs the user has access to
  const availableCanonicalKeys = useMemo((): Set<string> => {
    const keys = new Set<string>();
    for (const canon of CANONICAL_ROLES) {
      if (canon.mappedRoles.some((r) => userRolesSet.has(r))) {
        keys.add(canon.key);
      }
    }
    return keys;
  }, [userRolesSet]);

  const allActive = sessionActiveRoles === null;
  const activeSet = new Set(sessionActiveRoles || Array.from(userRolesSet));

  const toggleAll = () => {
    if (allActive) return;
    setSessionActiveRoles(null);
    if (activeProfile) {
      navigate(getProfileDashboardPath(activeProfile));
    } else if (user?.role) {
      navigate(getRoleDashboardPath(user.role));
    }
  };

  const toggleCanonicalRole = async (canonKey: string) => {
    const canon = CANONICAL_ROLES.find((c) => c.key === canonKey);
    if (!canon) return;

    // Find the actual UserRole the user has for this canonical tab
    const effectiveRole: UserRole =
      canon.mappedRoles.find((r) => userRolesSet.has(r)) ?? (canon.mappedRoles[0] as UserRole);

    // If already the only selected role — switch to ALL
    if (sessionActiveRoles?.length === 1 && canon.mappedRoles.some((r) => activeSet.has(r))) {
      setSessionActiveRoles(null);
      return;
    }

    // Prefer the currently active same-role profile, then the last gym/role
    // selection (multi-gym owners), then the first matching profile.
    const targetProfile = findProfileForRole(
      profiles,
      effectiveRole,
      activeProfile,
      getRememberedProfileId(effectiveRole)
    );
    setSwitchingRole(effectiveRole);
    try {
      if (targetProfile) {
        await switchProfile(targetProfile.id);
      }

      // Select ONLY this role and navigate to its dashboard
      setSessionActiveRoles([effectiveRole]);
      navigate(getRoleDashboardPath(effectiveRole));
    } catch (error) {
      console.error('Failed to switch role profile:', error);
    } finally {
      setSwitchingRole(null);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="hidden lg:flex items-center gap-1">
        {/* ALL button */}
        <button
          type="button"
          onClick={toggleAll}
          className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
            allActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground/70 hover:bg-muted/80'
          }`}
        >
          ALL
        </button>

        {/* Canonical role tabs */}
        {CANONICAL_ROLES.map((canon) => {
          const isAvailable = availableCanonicalKeys.has(canon.key);
          const isActive = allActive || canon.mappedRoles.some((r) => activeSet.has(r));

          if (!isAvailable) {
            // Greyed-out tab with role-specific tooltip and optional link (opens new tab)
            const tabContent = (
              <span className="px-3 py-1.5 text-sm font-medium rounded-md bg-muted/20 text-foreground/25 select-none cursor-pointer hover:text-foreground/40 transition-colors">
                {canon.label}
              </span>
            );

            return (
              <Tooltip key={canon.key}>
                <TooltipTrigger asChild>
                  {canon.disabledLink ? (
                    <a
                      href={canon.disabledLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                    >
                      {tabContent}
                    </a>
                  ) : (
                    tabContent
                  )}
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {canon.disabledTooltip}
                  {canon.disabledLink && (
                    <span className="block text-primary mt-0.5">Click to find out more</span>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          }

          // Available tab — clickable
          return (
            <button
              key={canon.key}
              type="button"
              onClick={() => toggleCanonicalRole(canon.key)}
              disabled={switchingRole !== null}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-muted/50 text-foreground/60 border border-transparent hover:bg-muted/80 hover:text-foreground'
              }`}
            >
              {canon.label}
            </button>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
